// Rudras-AI Commerce Engine — Stripe redirect-back handler (Mar 2026).
//
// Stripe Checkout redirects here on success/cancel with a
// `payment_id=...&session_id=...` query string. We poll the engine
// status endpoint until it flips to `paid` (or `failed`), then send
// the customer to the order-progress screen.
//
// Why poll instead of trusting the redirect? Webhooks can race the
// browser; the engine's `/status` endpoint already falls back to
// `provider.lookup_status` for an in-line truth-check.
//
// Parcel Shipping Checkout Enforcement — this component is mounted on
// TWO distinct routes (`/retail/:slug/payment/return` and
// `/retail/:slug/payment/cancelled`), and used to treat both
// identically: no `payment_id` in the URL always fell through to the
// "Couldn't confirm payment / Missing payment_id" technical error.
// That's correct for `/payment/return` (a payment_id is genuinely
// required there), but `/payment/cancelled` is Stripe's own
// `cancel_url` — it NEVER carries a payment_id by design, and a
// customer voluntarily backing out of Stripe is not a confirmation
// failure. The two paths now branch explicitly.

import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ShoppingCart, Clock } from "lucide-react";
import { customerKey, loadLinkSlug, saveLinkSlug, storefrontPath, clearVerifiedProof } from "../lib/retailSessionKeys";
import { logCustomerAuditEvent } from "../lib/customerAuditLog";
import LocalDeliveryRecovery from "../components/retail/customer/LocalDeliveryRecovery";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RETAIL_BASE = `${BACKEND_URL}/api/retail/public`;
const MAX_POLLS = 15;
const POLL_MS = 1500;
// Endless-spinner correction (Aug 2026) — once payment has been
// received and the page is only waiting on delivery PROGRESS (not
// payment confirmation), a real delivery can take many minutes —
// nothing like `MAX_POLLS`/`POLL_MS` above's ~22s budget for
// confirming payment itself. Confirmed defect this closes: `awaiting_
// delivery`/`delivery_confirmed` used to share that same ~22s budget,
// so a real dispatch that took longer silently stopped polling while
// still showing an animated spinner — the page froze mid-spin forever,
// even after the delivery reached `delivered` server-side, because no
// further request was ever sent to find out. This longer, looser
// budget applies for the ENTIRE post-payment tracking phase
// (`awaiting_delivery` through `delivery_confirmed`) so it never falls
// through to the payment-confirmation timeout error; exhausting it
// instead reaches the explicit, non-alarming `tracking_timeout` state
// below (never a silently-frozen spinner).
const DELIVERY_TRACKING_MAX_POLLS = 60;
const DELIVERY_TRACKING_POLL_MS = 5000;

// Auto-transition correction (Aug 2026, UBE-ORD-000029) — confirmed
// defect: a customer who left the "Authorization received" page open
// while the tenant captured never saw the page update; it silently
// stayed on the authorization screen forever because `authorized_
// awaiting_review` was treated as terminal (see the removal from
// `TERMINAL_STATUSES` below) and the poll loop simply stopped. This
// phase keeps polling — read-only, low-frequency — for as long as the
// page stays open and the order is genuinely still awaiting review, so
// a tenant capture is picked up and the page auto-transitions to the
// paid order-status screen without a manual refresh. Deliberately
// UNBOUNDED (no max-poll exhaustion state): review can legitimately
// take a long time and "authorized_awaiting_review" is itself already
// a truthful, non-alarming screen — there is nothing better to fall
// back to. The interval backs off substantially while the tab is
// hidden, since nothing needs to update for a customer who isn't
// looking.
const AUTH_WAIT_POLL_MS = 5000;
const AUTH_WAIT_HIDDEN_POLL_MS = 20000;

// States reachable only after payment is already confirmed, where the
// page is purely waiting on delivery progress — these share the long
// tracking budget/interval above (never the short payment-confirmation
// one) and are the only states `tracking_timeout` can be reached from.
const TRACKING_PHASE_STATUSES = new Set(["awaiting_acceptance", "awaiting_delivery", "delivery_confirmed"]);

// Every status the poll loop must never resume from or regress out of.
// Used as a functional-setState guard so a late/stale response can
// never overwrite a newer, already-terminal state (e.g. a delayed
// `courier_requested`-era response arriving after `delivered` was
// already reached from a later request).
const TERMINAL_STATUSES = new Set([
  "paid", "failed", "cancelled", "refunded", "partial_refund",
  "reconciliation_required", "delivered", "malformed", "timeout",
  // Follow-up correction (Aug 2026) — the authorization is dead
  // either way (tenant declined, or Stripe released it on its own);
  // nothing further will ever happen for this payment attempt.
  // `capture_processing` is deliberately NOT terminal — see the poll
  // loop's own handling, it keeps polling on the short budget.
  "authorization_released", "authorization_expired",
  // NOTE: `authorized_awaiting_review` is intentionally NOT listed
  // here (see the auto-transition correction above) — it is the
  // successful outcome of the initial Checkout confirmation, but NOT
  // terminal for the overall customer order lifecycle, since the
  // tenant capturing afterward must still flip this page to `paid`.
]);

export default function RetailPaymentReturn() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const paymentId = params.get("payment_id");
  const isCancelRoute = location.pathname.endsWith("/payment/cancelled");

  // Parcel Shipping Checkout Enforcement — the query param is the
  // authoritative signal (it just survived the actual Stripe round
  // trip); sessionStorage is the fallback for any edge case where the
  // param is somehow stripped. Re-persist whichever wins so subsequent
  // navigation this session stays consistent.
  const linkSlugFromQuery = params.get("link_slug") || "";
  const returnLinkSlug = linkSlugFromQuery || loadLinkSlug(slug);
  useEffect(() => {
    if (linkSlugFromQuery) saveLinkSlug(slug, linkSlugFromQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, linkSlugFromQuery]);
  const backTarget = returnLinkSlug ? `/retail/${slug}/${returnLinkSlug}` : storefrontPath(slug);

  const [state, setState] = useState(() => (
    isCancelRoute ? { status: "cancelled_voluntary", error: "", payment: null } : { status: "polling", error: "", payment: null }
  ));
  const [attempts, setAttempts] = useState(0);
  // Separate poll counter for the post-payment delivery-tracking phase
  // (`awaiting_delivery` / `delivery_confirmed`), so it gets its own
  // longer budget/interval (`DELIVERY_TRACKING_MAX_POLLS`/`_POLL_MS`,
  // used by the single unified poll effect below) instead of sharing
  // the short payment-confirmation budget.
  const [trackingAttempts, setTrackingAttempts] = useState(0);
  // Auto-transition correction (Aug 2026) — separate poll counter for
  // the `authorized_awaiting_review` wait phase, kept independent of
  // both `attempts` (short payment-confirmation budget) and
  // `trackingAttempts` (post-payment delivery tracking). Deliberately
  // never checked against a max — see `AUTH_WAIT_POLL_MS`'s own
  // comment for why this phase is unbounded.
  const [authAttempts, setAuthAttempts] = useState(0);

  // Auto-transition correction (Aug 2026) — guards against the "paid"
  // branch below scheduling more than one navigation. A late/duplicate
  // "paid" response arriving after the page has already scheduled its
  // move to the order-status screen must not re-schedule it.
  const navigatedRef = useRef(false);
  // Holds the pending post-"paid" navigate timeout so it can be
  // cancelled if the customer navigates away before it fires — this is
  // cleared ONLY on true component unmount (see the dedicated effect
  // below), never by the polling effect's own per-render cleanup,
  // since that would cancel the very navigation it just scheduled the
  // instant `state.status` flips to "paid" and re-triggers the effect.
  const navigateTimeoutRef = useRef(null);
  useEffect(() => () => {
    if (navigateTimeoutRef.current) clearTimeout(navigateTimeoutRef.current);
  }, []);

  // Voluntary cancellation — never polls, never requires payment_id.
  // Logged once on mount.
  useEffect(() => {
    if (!isCancelRoute) return;
    logCustomerAuditEvent(slug, {
      event: "stripe_checkout_cancelled", reasonCode: "voluntary_cancel",
      catalogId: undefined, meta: { has_link_slug: Boolean(returnLinkSlug) },
    });
    if (returnLinkSlug) {
      logCustomerAuditEvent(slug, { event: "payment_return_context_restored", reasonCode: "voluntary_cancel" });
    } else {
      // Neither the URL nor sessionStorage had a link_slug — "Continue
      // checkout" is about to fall back to the bare tenant storefront
      // instead of the catalog the customer actually checked out from.
      logCustomerAuditEvent(slug, { event: "catalog_context_lost", reasonCode: "voluntary_cancel" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCancelRoute]);

  // Endless-spinner correction (Aug 2026) — a single, unified poll loop
  // (replacing the previous two-effect split) so every status
  // transition below is written once, reachable from every phase, and
  // cannot regress a terminal state reached by a newer request. Its
  // interval/budget is state-dependent: the short `MAX_POLLS`/
  // `POLL_MS` budget applies only during the initial `polling`/
  // `under_review` payment-confirmation phase; `awaiting_delivery` and
  // `delivery_confirmed` both use the long `DELIVERY_TRACKING_*`
  // budget via the shared `trackingAttempts` counter (see the
  // constants' own comments for why).
  useEffect(() => {
    if (isCancelRoute) return undefined;
    let cancelled = false;
    const isTrackingPhase = TRACKING_PHASE_STATUSES.has(state.status);
    const isAuthWaitPhase = state.status === "authorized_awaiting_review";

    async function tick() {
      if (!paymentId) {
        // A genuinely malformed /payment/return URL — kept as a
        // distinct technical-error state, never merged with voluntary
        // cancellation (which never reaches this branch at all).
        setState({ status: "malformed", error: "Missing payment_id in redirect URL." });
        return;
      }
      try {
        const { data } = await axios.get(`${RETAIL_BASE}/${slug}/payments/${paymentId}/status`);
        if (cancelled) return;
        if (data.status === "paid") {
          setState((s) => (TERMINAL_STATUSES.has(s.status) ? s : { status: "paid", payment: data }));
          // Feb 2026 — close the customer session on successful
          // payment so the shopper can't accidentally resume the
          // just-paid cart if they navigate back. The receipt +
          // "Start New Order" CTAs on the order status page are the
          // only sanctioned next steps.
          try {
            sessionStorage.removeItem("retail_session_id");
            // PR #41 hardening — this removed the UNSCOPED legacy key,
            // a stale reference from before the cross-tenant session-
            // leak fix scoped it to `retail_customer_v1:{slug}` — "end
            // the session after payment" silently did nothing.
            sessionStorage.removeItem(customerKey(slug));
            // Shared-kiosk correction (Aug 2026) — a verified member's
            // Favorites proof is part of the SAME "customer session"
            // this comment already ends on successful payment. Without
            // this, the next person on a shared kiosk tablet could
            // still see the just-departed customer's Favorites tab via
            // the refresh-restoration path, even though their order is
            // fully complete and the session is meant to be over.
            clearVerifiedProof(slug);
          } catch (_e) { /* noop */ }
          // Soft-redirect to the order progress page after a moment so the
          // customer sees the green "Paid" confirmation. Guarded so a
          // duplicate/late "paid" response (e.g. this same status
          // reached from `authorized_awaiting_review` after the tenant
          // captures) can never schedule a second navigation.
          if (!navigatedRef.current) {
            navigatedRef.current = true;
            navigateTimeoutRef.current = setTimeout(
              () => navigate(`/retail/${slug}/orders/${data.order_code || data.order_id}`, { replace: true }),
              1200,
            );
          }
          return;
        }
        // Tenant-Review Return Recognition Correction (Aug 2026,
        // UBE-ORD-000027) — for a tenant-review (manual-capture) order,
        // Stripe Checkout completing means the card is AUTHORIZED and
        // HELD, never charged yet — the restaurant reviews and captures
        // (or releases) it afterward. This is the authoritative,
        // successful outcome of the initial Checkout return: no
        // session-close, no redirect (nothing paid to show a receipt
        // for yet), never imply failure. No `return` here, unlike the
        // other branches — falls through to the scheduling logic below,
        // which keeps polling (see the auto-transition correction
        // above) so a subsequent tenant capture still reaches `paid`.
        if (data.status === "authorized_awaiting_review") {
          setState((s) => (TERMINAL_STATUSES.has(s.status) ? s : { status: "authorized_awaiting_review", payment: data }));
        }
        // Follow-up correction (Aug 2026) — `capture_processing` is
        // NOT the same fact as "awaiting review": the restaurant has
        // already accepted the order and a real Stripe capture call is
        // in flight. Never says the restaurant is still reviewing,
        // never invites another approval, never claims the payment was
        // captured yet — this is expected to resolve to `paid` (or,
        // rarely, `reconciliation_required`) within moments, so it
        // keeps polling on the short budget exactly like `payment_
        // under_review` does, rather than being treated as terminal.
        if (data.status === "capture_processing") {
          setState((s) => (TERMINAL_STATUSES.has(s.status) || s.status === "capture_processing" ? s : { status: "capture_processing", error: "", payment: null }));
        }
        // Follow-up correction (Aug 2026) — the tenant actively
        // declined this order, or (distinctly) Stripe released the
        // authorization on its own before the tenant ever acted. Never
        // charged either way. Terminal — nothing further to poll for.
        if (data.status === "authorization_released" || data.status === "authorization_expired") {
          setState((s) => (TERMINAL_STATUSES.has(s.status) ? s : { status: data.status, payment: data }));
          return;
        }
        if (data.status === "failed" || data.status === "cancelled") {
          setState((s) => (TERMINAL_STATUSES.has(s.status) ? s : { status: data.status, payment: data }));
          return;
        }
        // Aug 2026 — quote-expiry-vs-payment safety net. Before this,
        // `refunded`/`partial_refund` matched neither branch above and
        // fell through to poll-exhaustion after ~22s, landing on the
        // generic "Couldn't confirm payment... your order is safely
        // saved" message — actively wrong for a payment the backend
        // already refunded because the delivery couldn't be arranged.
        // Reachable from EVERY non-terminal phase, including
        // `awaiting_delivery`/`delivery_confirmed` — a delivery that
        // ultimately couldn't be dispatched can still resolve to a
        // refund after the customer has already moved past the
        // initial payment-confirmation screen.
        if (data.status === "refunded" || data.status === "partial_refund") {
          setState((s) => (TERMINAL_STATUSES.has(s.status) ? s : { status: data.status, payment: data }));
          return;
        }
        // Round 2 (independent review of PR #100) — `payment_under_
        // review` is the honest, transient state a Local Delivery
        // payment sits in while dispatch-fulfillability is verified
        // (see `payments/engine.py::_flag_local_delivery_order_paid`).
        // It resolves to `paid`/`refunded`/`reconciliation_required` in
        // the SAME backend request in the overwhelming majority of
        // cases — this branch exists so a customer who polls DURING
        // that (normally sub-second) window sees an accurate "verifying
        // your delivery" message instead of the generic "confirming
        // your payment" copy, and so a poll that somehow lands before
        // resolution keeps polling rather than falling through to
        // poll-exhaustion. Deliberately does NOT return — falls through
        // to the ordinary attempt-counter increment below, same as the
        // catch-all "still pending" path.
        if (data.status === "payment_under_review") {
          setState((s) => (TERMINAL_STATUSES.has(s.status) || s.status === "under_review" ? s : { status: "under_review", error: "", payment: null }));
        }
        // A Local Delivery payment this codebase could not automatically
        // resolve (a failed/ambiguous-even-after-reconciliation refund,
        // or a dispatch-workflow outcome outside the well-tested
        // quote-expiry case) — NEVER shown as an ordinary successful
        // order, and NEVER silently left polling forever. Also
        // reachable from `awaiting_delivery`/`delivery_confirmed`, same
        // reasoning as the refund branch above.
        if (data.status === "reconciliation_required") {
          setState((s) => (TERMINAL_STATUSES.has(s.status) ? s : { status: "reconciliation_required", payment: data }));
          return;
        }
        // Hotfix (Aug 2026, UBE-ORD-000017) — payment genuinely
        // captured for an internal_test order, correctly held back from
        // ordinary fulfillment while it waits for the existing
        // controlled Preview/Confirm dispatch authorization step (an
        // expected, staging-only wait, never an error). Distinct from
        // both "paid" (fulfillment not yet safe to release) and
        // "reconciliation_required" (nothing is actually wrong here).
        //
        // Go-live correction (Aug 2026), Scope F — this used to be
        // TERMINAL for the poll loop (a customer stuck here never saw
        // anything else without manually reloading, and the copy used
        // internal "test"/"controlled dispatch"/"authorization"
        // terminology). Now maps to the customer-safe `awaiting_
        // delivery` UI state and keeps polling until either
        // `data.delivery_status` reports progress (see below) or one
        // of the terminal outcomes above (refund/reconciliation) is
        // reached.
        if (data.status === "paid_awaiting_test_dispatch_authorization") {
          setState((s) => (TERMINAL_STATUSES.has(s.status) || s.status === "awaiting_delivery" ? s : { status: "awaiting_delivery", error: "", payment: data }));
        }
        // Tenant-owned production dispatch (Aug 2026) — the regular-
        // order counterpart to the controlled-TEST wait above. Payment
        // captured; the restaurant has not yet reviewed/accepted this
        // order, so no courier has been requested. Distinct customer-
        // facing state from `awaiting_delivery` (which implies the
        // restaurant has already acted and a courier is being
        // arranged) — conflating the two would tell the customer
        // something not yet true. Once the tenant accepts,
        // `payments/engine.py` moves the order through `tenant_accepted`
        // (aliased server-side to `payment_under_review`) and then to a
        // dispatch outcome; this branch simply stops matching and the
        // existing `awaiting_delivery`/`delivery_confirmed` branches
        // take over exactly as they already do for the TEST flow.
        if (data.status === "awaiting_tenant_acceptance") {
          setState((s) => (TERMINAL_STATUSES.has(s.status) || s.status === "awaiting_acceptance" ? s : { status: "awaiting_acceptance", error: "", payment: data }));
        }
        // Go-live correction (Aug 2026), Scope F — once Uber has
        // genuinely acknowledged the delivery (the SAME normalized
        // stage `dispatch_service.py`'s own `_CONFIRMED_DISPATCH_
        // STATUSES` uses) AND a real tracking record exists
        // (`delivery_status`/`tracking_url`, computed server-side by
        // `routes/payments.py`'s `/status` route — never inferred
        // client-side), transition to the truthful "Delivery
        // confirmed" state. Resets the shared tracking-phase counter
        // only on the actual transition INTO this state, giving the
        // now-confirmed delivery a fresh full budget of its own.
        if (data.delivery_status === "confirmed") {
          setState((s) => {
            if (TERMINAL_STATUSES.has(s.status)) return s;
            if (s.status !== "delivery_confirmed") setTrackingAttempts(0);
            return { status: "delivery_confirmed", payment: data };
          });
          return;
        }
        if (data.delivery_status === "delivered") {
          // Endless-spinner correction — a stale response already
          // reporting `delivered` is idempotent to apply; a functional
          // guard is still used so this can never fire twice or race
          // with a state change already in flight.
          setState((s) => (s.status === "delivered" ? s : { status: "delivered", payment: data }));
          return;
        }
      } catch (e) {
        if (cancelled) return;
        // Transient — keep polling/tracking.
      }
      if (isTrackingPhase) setTrackingAttempts((n) => n + 1);
      else if (isAuthWaitPhase) setAuthAttempts((n) => n + 1);
      else setAttempts((n) => n + 1);
    }

    const pollCount = isTrackingPhase ? trackingAttempts : (isAuthWaitPhase ? authAttempts : attempts);
    // Auto-transition correction — `isAuthWaitPhase` has no finite
    // budget (`Infinity`), so it never reaches the exhaustion branch
    // below; see `AUTH_WAIT_POLL_MS`'s own comment for why.
    const maxPolls = isTrackingPhase ? DELIVERY_TRACKING_MAX_POLLS : (isAuthWaitPhase ? Infinity : MAX_POLLS);
    const pollMs = isTrackingPhase
      ? DELIVERY_TRACKING_POLL_MS
      : isAuthWaitPhase
        ? ((typeof document !== "undefined" && document.hidden) ? AUTH_WAIT_HIDDEN_POLL_MS : AUTH_WAIT_POLL_MS)
        : POLL_MS;
    const stillActive = state.status === "polling" || state.status === "under_review"
      || state.status === "capture_processing" || isTrackingPhase || isAuthWaitPhase;

    if (pollCount < maxPolls && stillActive) {
      const immediate = !isTrackingPhase && !isAuthWaitPhase && attempts === 0;
      const h = setTimeout(tick, immediate ? 0 : pollMs);
      return () => { cancelled = true; clearTimeout(h); };
    }
    if (pollCount >= maxPolls) {
      if (state.status === "polling" || state.status === "under_review" || state.status === "capture_processing") {
        setState({ status: "timeout", error: "We couldn't confirm your payment in time. Please refresh — your order is safely saved." });
      } else if (isTrackingPhase) {
        // Endless-spinner correction — the confirmed defect this
        // closes: exhausting the tracking budget used to just stop
        // polling silently, leaving the animated `awaiting_delivery`
        // spinner frozen on screen forever (even long after the
        // delivery had genuinely reached `delivered` server-side,
        // since nothing was left to find out). Never implies a
        // payment or delivery failure — the "Check status" action
        // below simply resumes tracking for another full budget.
        setState((s) => (TRACKING_PHASE_STATUSES.has(s.status) ? { ...s, status: "tracking_timeout" } : s));
      }
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempts, trackingAttempts, authAttempts, paymentId, slug, isCancelRoute, state.status]);

  // Endless-spinner correction — manual resume for the bounded
  // `tracking_timeout` state. Restarts the SAME tracking-phase poll
  // loop above (fresh full budget) at whichever stage it was actually
  // waiting on, determined from the last response this page received
  // rather than assumed.
  const checkStatusNow = () => {
    setTrackingAttempts(0);
    setState((s) => ({
      ...s,
      status: s.payment?.delivery_status === "confirmed" ? "delivery_confirmed" : "awaiting_delivery",
    }));
  };

  const startNewOrder = () => {
    try {
      const fresh = `r-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
      sessionStorage.setItem("retail_session_id", fresh);
      sessionStorage.removeItem(customerKey(slug));
      // Shared-kiosk correction (Aug 2026) — see the matching comment
      // above; "Start New Order" from this page must end a verified
      // member's Favorites session exactly like it already ends the
      // saved customer record.
      clearVerifiedProof(slug);
    } catch (_e) { /* noop */ }
    navigate(backTarget, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0d1f12] text-stone-100 grid place-items-center px-4" data-testid="retail-payment-return">
      <div className="w-full max-w-md bg-emerald-950/50 border border-emerald-200/15 rounded-2xl p-6 text-center">
        {state.status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-emerald-300 animate-spin" />
            <h2 className="mt-4 font-serif text-2xl">Confirming your payment…</h2>
            <p className="mt-2 text-emerald-200/70 text-sm">This usually takes a moment.</p>
          </>
        )}
        {/* Round 2 (independent review of PR #100) — honest, distinct
            from the generic "Confirming your payment…" copy above: the
            payment itself already succeeded, but for a Local Delivery
            order this codebase never shows an ordinary "paid"/"Order
            Received" state until dispatch-fulfillability (is the
            accepted quote still valid?) is confirmed. Never claims the
            order is placed/received while this is showing. */}
        {state.status === "under_review" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-emerald-300 animate-spin" data-testid="retail-payment-under-review" />
            <h2 className="mt-4 font-serif text-2xl">Verifying your delivery…</h2>
            <p className="mt-2 text-emerald-200/70 text-sm">
              Your payment was received — we're just confirming your courier can still be dispatched.
            </p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-300" />
            <h2 className="mt-4 font-serif text-2xl">Payment received</h2>
            <p className="mt-2 text-emerald-200/70 text-sm">Taking you to your order…</p>
          </>
        )}
        {/* Tenant-Review Return Recognition Correction (Aug 2026,
            UBE-ORD-000027) — the successful checkout-return outcome for
            a tenant-review (manual-capture) order. The card is
            authorized and held, never charged yet; the restaurant
            reviews the order and captures the final amount afterward.
            Deliberately never says "Payment received"/"Paid" (that
            would be false) and never implies anything went wrong. */}
        {state.status === "authorized_awaiting_review" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-300" data-testid="retail-payment-authorized-awaiting-review" />
            <h2 className="mt-4 font-serif text-2xl">Authorization received</h2>
            <p className="mt-2 text-emerald-200/70 text-sm">
              Your card has been authorized and held — you have not been charged yet.
              The restaurant is reviewing your order now; your card will only be charged
              once they confirm it.
            </p>
            {(state.payment?.order_code || state.payment?.order_id) && (
              <p className="mt-2 text-emerald-200/70 text-sm">
                Order {state.payment.order_code || state.payment.order_id}
              </p>
            )}
            <Link
              to={backTarget}
              className="mt-4 inline-block text-sm text-emerald-200/80 underline hover:text-emerald-100"
              data-testid="retail-payment-back-after-authorization"
            >Back to menu</Link>
          </>
        )}
        {/* Follow-up correction (Aug 2026) — the restaurant has ALREADY
            accepted the order (never re-invite another approval here)
            and a real Stripe capture call is in flight. Deliberately
            distinct copy from `authorized_awaiting_review` above:
            never says "reviewing," never claims the payment is
            captured yet — only that confirmation is being finalized.
            Non-terminal; resolves to `paid` (or rarely `reconciliation_
            required`) within moments without any customer action. */}
        {state.status === "capture_processing" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-emerald-300 animate-spin" data-testid="retail-payment-capture-processing" />
            <h2 className="mt-4 font-serif text-2xl">Finalizing your payment…</h2>
            <p className="mt-2 text-emerald-200/70 text-sm">
              Your order has been accepted — we're just finalizing the payment confirmation now.
            </p>
          </>
        )}
        {/* Follow-up correction (Aug 2026) — the tenant actively
            declined this order. Never charged; nothing to retry here. */}
        {state.status === "authorization_released" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-amber-300" data-testid="retail-payment-authorization-released" />
            <h2 className="mt-4 font-serif text-2xl">Order not accepted</h2>
            <p className="mt-2 text-stone-300 text-sm">
              The restaurant was unable to accept your order. Your card was authorized
              but never charged — the hold has been released.
            </p>
            <Link
              to={backTarget}
              className="mt-4 inline-block text-sm text-emerald-200/80 underline hover:text-emerald-100"
              data-testid="retail-payment-back-after-release"
            >Back to menu</Link>
          </>
        )}
        {/* Follow-up correction (Aug 2026) — Stripe released the hold
            on its own before the tenant ever acted (its own ~7-day
            uncaptured-authorization expiry). Distinct from an active
            tenant decline — same financial outcome (never charged),
            different cause. */}
        {state.status === "authorization_expired" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-amber-300" data-testid="retail-payment-authorization-expired" />
            <h2 className="mt-4 font-serif text-2xl">Authorization expired</h2>
            <p className="mt-2 text-stone-300 text-sm">
              Your card authorization expired before your order could be reviewed. You have
              not been charged.
            </p>
            <Link
              to={backTarget}
              className="mt-4 inline-block text-sm text-emerald-200/80 underline hover:text-emerald-100"
              data-testid="retail-payment-back-after-expiry"
            >Back to menu</Link>
          </>
        )}
        {/* Honest voluntary-cancellation state — not a confirmation failure. */}
        {state.status === "cancelled_voluntary" && (
          <>
            <ShoppingCart className="w-12 h-12 mx-auto text-amber-300" />
            <h2 className="mt-4 font-serif text-2xl" data-testid="retail-payment-cancelled-title">Payment cancelled</h2>
            <p className="mt-2 text-stone-300 text-sm">
              Your payment was not completed.<br />Your cart has been preserved.
            </p>
            <div className="mt-5 flex flex-col gap-2">
              <Link
                to={backTarget}
                className="px-5 py-2.5 rounded-full bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 active:scale-[0.98]"
                data-testid="retail-payment-continue-checkout"
              >Continue checkout</Link>
              <button
                type="button"
                onClick={startNewOrder}
                className="px-5 py-2.5 rounded-full border border-emerald-200/30 text-emerald-100 text-sm hover:bg-black/30"
                data-testid="retail-payment-start-new"
              >Start new order</button>
            </div>
          </>
        )}
        {/* Aug 2026 — quote-expiry-vs-payment safety net. Distinct from
            the generic error block below: this is NOT a technical
            failure — the payment succeeded and was then deliberately,
            automatically refunded because delivery could not be
            arranged. The order THIS refund applied to is never resumed
            as-is (its accepted quote/session is retired) — but Round 3
            (independent review of PR #100) adds a genuine same-order
            recovery path: when the backend confirms recovery is
            currently permitted (`local_delivery_recovery.eligible`,
            computed server-side by `routes/payments.py`'s `/status`
            route — never trusted from anywhere else), a fresh delivery
            quote can be requested and paid for on THIS SAME order,
            never a duplicate one. "Start new order" remains available
            underneath as the always-safe fallback. */}
        {(state.status === "refunded" || state.status === "partial_refund") && (
          <>
            <ShoppingCart className="w-12 h-12 mx-auto text-amber-300" />
            <h2 className="mt-4 font-serif text-2xl" data-testid="retail-payment-refunded-title">
              Payment refunded
            </h2>
            <p className="mt-2 text-stone-300 text-sm" data-testid="retail-payment-refunded-message">
              We weren't able to arrange delivery for this order in time, so your
              payment has been {state.status === "partial_refund" ? "partially " : ""}
              refunded.
              {state.payment?.local_delivery_recovery?.eligible
                ? " You can get a new delivery quote for this same order below, or place a new order instead."
                : " Please place a new order when you're ready."}
            </p>
            {state.payment?.local_delivery_recovery?.eligible && (
              <LocalDeliveryRecovery
                slug={slug}
                orderId={state.payment.order_id}
                orderCode={state.payment.order_code}
              />
            )}
            <button
              type="button"
              onClick={startNewOrder}
              className="mt-3 px-5 py-2.5 rounded-full bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 active:scale-[0.98]"
              data-testid="retail-payment-start-new-after-refund"
            >Start new order</button>
            {/* Initial go-live completion pass (Aug 2026) — a customer
                who closes this tab previously had no way back to this
                (or any other) order. `WelcomeModal.jsx`'s "View my past
                orders" affordance (`PastOrdersLookup.jsx`) is reachable
                from the storefront once a recognized phone is entered. */}
            <Link
              to={backTarget}
              className="mt-2 text-sm text-emerald-200/80 underline hover:text-emerald-100"
              data-testid="retail-payment-lookup-orders-after-refund"
            >Look up your orders</Link>
          </>
        )}
        {/* Round 2 (independent review of PR #100), broadened by the
            follow-up correction (Aug 2026) — this status now covers
            TWO distinct real causes sharing the same wire value: a
            Local Delivery payment this codebase could not
            automatically resolve, AND a tenant-review capture whose
            Stripe outcome came back genuinely ambiguous
            (`capture_authorized_payment`'s own `ambiguous` branch —
            never retry blindly, since we do not actually know whether
            the money moved). Copy is deliberately cause-neutral and
            never claims a specific charge/delivery fact we can't
            confirm, and never invites the customer to try paying
            again — the existing card can't be re-authorized from this
            screen, and a definitive answer only comes from our team. */}
        {state.status === "reconciliation_required" && (
          <>
            <ShoppingCart className="w-12 h-12 mx-auto text-amber-300" />
            <h2 className="mt-4 font-serif text-2xl" data-testid="retail-payment-reconciliation-title">
              We're reviewing your order
            </h2>
            <p className="mt-2 text-stone-300 text-sm" data-testid="retail-payment-reconciliation-message">
              We ran into an issue confirming the final status of your order and payment, and our team
              is looking into it now. Please do not attempt to pay again — no action is needed from you,
              and we will reach out if anything further is required.
            </p>
            <Link
              to={backTarget}
              className="mt-3 inline-block text-sm text-emerald-200/80 underline hover:text-emerald-100"
              data-testid="retail-payment-lookup-orders-after-reconciliation"
            >Look up your orders</Link>
          </>
        )}
        {/* Hotfix (Aug 2026, UBE-ORD-000017) — genuinely truthful, not
            an error state: this is the expected wait between payment and
            the courier being dispatched. Never says "Received"
            (fulfillment is deliberately not released yet) and never
            implies anything is wrong.

            Go-live correction (Aug 2026), Scope F — confirmed defect:
            this state previously used internal terminology ("test
            payment", "controlled delivery-dispatch authorization") that
            a real customer should never see, and never progressed past
            this screen even after Uber accepted the delivery. Renamed
            from `paid_awaiting_test_dispatch_authorization` to the
            customer-safe `awaiting_delivery`; copy rewritten to say only
            what's true and useful to the customer. The poll loop above
            now carries this state forward to `delivery_confirmed` /
            `delivered` once the backend reports real progress.

            Endless-spinner correction (Aug 2026) — confirmed defect:
            this used a permanently-animated `Loader2` here, which kept
            spinning even after the tracking budget silently exhausted
            (see `tracking_timeout` below) or after the delivery had
            already reached `delivered` server-side. A stable, static
            icon reflects what's actually true — payment received,
            nothing currently loading — rather than implying an
            in-progress network request that may no longer exist. */}
        {/* Tenant-owned production dispatch (Aug 2026) — reached the
            moment payment is verified, before the restaurant has
            reviewed the order. Never implies a courier is already
            being arranged (that is not yet true) — distinct wording
            from `awaiting_delivery` below intentionally. */}
        {state.status === "awaiting_acceptance" && (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-300" data-testid="retail-payment-awaiting-acceptance" />
            <h2 className="mt-4 font-serif text-2xl">Payment received — order awaiting confirmation</h2>
            <p className="mt-2 text-emerald-200/70 text-sm">
              Your payment was successful. The restaurant is reviewing your order now — this page
              will update automatically once your delivery is on its way.
            </p>
          </>
        )}
        {state.status === "awaiting_delivery" && (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-300" data-testid="retail-payment-awaiting-delivery" />
            <h2 className="mt-4 font-serif text-2xl">Payment received — arranging your delivery</h2>
            <p className="mt-2 text-emerald-200/70 text-sm">
              Your payment was successful. We're arranging your delivery now — this page will
              update automatically with tracking details once a courier is on the way.
            </p>
          </>
        )}
        {/* Go-live correction (Aug 2026), Scope F — reached once Uber has
            genuinely accepted the delivery AND a real tracking record
            exists server-side (never inferred client-side, never shown
            before a provider delivery ID actually exists). Shows the
            order reference, the current customer-friendly delivery
            status, and a tracking link when one is available. Stays on
            screen (with its sub-status refreshing) until `delivered`. */}
        {state.status === "delivery_confirmed" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-300" />
            <h2 className="mt-4 font-serif text-2xl" data-testid="retail-payment-delivery-confirmed-title">Delivery confirmed</h2>
            {(state.payment?.order_code || state.payment?.order_id) && (
              <p className="mt-2 text-emerald-200/70 text-sm">
                Order {state.payment.order_code || state.payment.order_id}
              </p>
            )}
            <p className="mt-2 text-emerald-200/70 text-sm" data-testid="retail-payment-delivery-status-label">
              {state.payment?.delivery_status_label || "Your courier is on the way."}
            </p>
            {state.payment?.tracking_url && (
              <a
                href={state.payment.tracking_url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="mt-3 inline-block text-sm text-emerald-200/80 underline hover:text-emerald-100"
                data-testid="retail-payment-tracking-link"
              >Track your delivery</a>
            )}
          </>
        )}
        {/* Go-live correction (Aug 2026), Scope F — the final, truthful
            terminal state once the courier has delivered the order. */}
        {state.status === "delivered" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-300" />
            <h2 className="mt-4 font-serif text-2xl" data-testid="retail-payment-delivered-title">Delivered</h2>
            {(state.payment?.order_code || state.payment?.order_id) && (
              <p className="mt-2 text-emerald-200/70 text-sm">
                Order {state.payment.order_code || state.payment.order_id}
              </p>
            )}
            <p className="mt-2 text-emerald-200/70 text-sm">Your order has been delivered. Enjoy!</p>
          </>
        )}
        {/* Endless-spinner correction (Aug 2026) — reached only after the
            long delivery-tracking budget (5 minutes) is exhausted while
            still `awaiting_delivery`/`delivery_confirmed`. Never implies
            a payment or delivery failure — the payment already
            succeeded and the delivery may well still be in progress;
            this only means automatic status refreshes have paused.
            "Check status" resumes tracking (fresh budget) from whichever
            stage the page was last actually at. */}
        {state.status === "tracking_timeout" && (
          <>
            <Clock className="w-10 h-10 mx-auto text-emerald-300" data-testid="retail-payment-tracking-timeout" />
            <h2 className="mt-4 font-serif text-2xl">Still arranging your delivery</h2>
            {(state.payment?.order_code || state.payment?.order_id) && (
              <p className="mt-2 text-emerald-200/70 text-sm">
                Order {state.payment.order_code || state.payment.order_id}
              </p>
            )}
            <p className="mt-2 text-emerald-200/70 text-sm">
              Your payment was successful and your order is safe. We're taking a bit longer than
              usual to refresh your delivery status automatically — tap below to check again.
            </p>
            {state.payment?.tracking_url && (
              <a
                href={state.payment.tracking_url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="mt-2 inline-block text-sm text-emerald-200/80 underline hover:text-emerald-100"
                data-testid="retail-payment-tracking-link"
              >Track your delivery</a>
            )}
            <button
              type="button"
              onClick={checkStatusNow}
              className="mt-4 px-5 py-2.5 rounded-full bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 active:scale-[0.98]"
              data-testid="retail-payment-check-status"
            >Check status</button>
          </>
        )}
        {(state.status === "malformed" || state.status === "failed" || state.status === "cancelled" || state.status === "timeout") && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-red-300" />
            <h2 className="mt-4 font-serif text-2xl">
              {state.status === "cancelled" ? "Payment cancelled" : "Couldn't confirm payment"}
            </h2>
            <p className="mt-2 text-stone-300 text-sm" data-testid="retail-payment-error">
              {state.error || "Please try again or speak to a staff member."}
            </p>
            <Link
              to={backTarget}
              className="mt-5 inline-block px-5 py-2.5 rounded-full bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 active:scale-[0.98]"
              data-testid="retail-payment-back"
            >Back to menu</Link>
          </>
        )}
      </div>
    </div>
  );
}
